import { useCallback, useLayoutEffect, useSyncExternalStore } from 'react';
import { useRouterState } from '@tanstack/react-router';
import {
  DARK_QUERY,
  NEXT_PREFERENCE,
  THEME_STORAGE_KEY,
  isLightOnlyPath,
  parsePreference,
  resolveTheme,
  type Theme,
  type ThemePreference,
} from './theme';

// A small external store, so every consumer sees the same choice. The language has
// i18next for that; the theme has nothing global, so it lives here.

function readStored(): ThemePreference {
  try {
    return parsePreference(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'light';
  }
}

function systemIsDark(): boolean {
  try {
    return window.matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
}

let preference: ThemePreference = readStored();
let lightOnly = isLightOnlyPath(window.location.pathname);
let applied: Theme = 'light';
const listeners = new Set<() => void>();

/** Retarget every token by setting `data-theme` on <html> (packages/tokens). */
function apply(): void {
  const next = lightOnly ? 'light' : resolveTheme(preference, systemIsDark());
  if (next !== applied || document.documentElement.getAttribute('data-theme') !== next) {
    applied = next;
    document.documentElement.setAttribute('data-theme', next);
    listeners.forEach((listener) => listener());
  }
}

// The inline script in index.html set the attribute before first paint; this
// asserts it again, in case that script did not run.
apply();

// *Systeem* follows the machine while the app is open: a laptop that turns dark in
// the evening turns the backoffice with it. The list is kept in a variable on
// purpose: a MediaQueryList nothing holds on to can be collected, and then its
// change never arrives.
let darkQuery: MediaQueryList | null = null;
try {
  darkQuery = window.matchMedia(DARK_QUERY);
  darkQuery.addEventListener('change', apply);
} catch {
  // No matchMedia: the choice still works, it just does not follow the machine.
}
// And whenever the window is looked at again. The event above does not arrive
// everywhere (a machine that turns dark while the laptop sleeps); looking is free.
window.addEventListener('focus', apply);
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) apply();
});

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getPreference = () => preference;
const getApplied = () => applied;

export function setThemePreference(next: ThemePreference): void {
  if (next === preference) return;
  preference = next;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // No storage: the theme still applies until the page is reloaded.
  }
  apply();
  listeners.forEach((listener) => listener());
}

/** The choice, the theme it comes down to now, and the next choice in the cycle. */
export function useTheme() {
  const themePreference = useSyncExternalStore(subscribe, getPreference, getPreference);
  const theme = useSyncExternalStore(subscribe, getApplied, getApplied);
  const cycleTheme = useCallback(
    () => setThemePreference(NEXT_PREFERENCE[themePreference]),
    [themePreference],
  );
  return { theme, themePreference, setThemePreference, cycleTheme };
}

/**
 * Keeps the theme right as the route changes: the portal is light whatever the
 * browser chose (`isLightOnlyPath`). Mounted once, in the root route. A layout
 * effect, so the portal never paints a frame in dark.
 */
export function useThemeForRoute(): void {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useLayoutEffect(() => {
    lightOnly = isLightOnlyPath(pathname);
    apply();
  }, [pathname]);
}
